/**
 * Score a match — the clubhouse Nassau.
 *
 * Two cards that are already in the app, a stroke or two given, and then you
 * walk the holes one at a time. It is deliberately a stepper rather than a
 * table: the whole point of settling up is the telling of it, and a grid of
 * final numbers throws away the part everyone actually enjoys.
 *
 * Press decisions live in component state. They are the only thing that cannot
 * be recovered from the cards, and they are answered once, at the table.
 */

import { useMemo, useState } from 'react'
import { runNassau, type PressDecision } from '../engine/nassau'
// `sliceFor` used to live here. It moved when the live board needed it too: the
// two surfaces must agree about which nine is the front, and two copies of that
// loop would eventually not.
import { autoStrokesFor, sliceFor } from '../engine/sideMatch'
import type { Tournament } from '../engine/types'
import { courseOf, playerName } from '../select'
import { mergedScores, useStore } from '../store'
import { Bar, SectionRule } from '../ui'

export function Nassau({ doc, onBack }: { doc: Tournament; onBack: () => void }) {
  const scores = useStore(mergedScores)
  const me = useStore((s) => s.session?.playerId ?? '')

  const [a, setA] = useState(me || doc.players[0]?.id || '')
  const [b, setB] = useState(doc.players.find((p) => p.id !== me)?.id ?? '')
  const [strokeMode, setStrokeMode] = useState<'auto' | 'manual'>('auto')
  const [giver, setGiver] = useState<'A' | 'B'>('A')
  const [strokeCount, setStrokeCount] = useState(0)
  const [amount, setAmount] = useState(20)
  const [roundIds, setRoundIds] = useState<string[]>(() =>
    doc.rounds.length ? [doc.rounds[0]!.id] : [],
  )
  const [started, setStarted] = useState(false)
  const [cursor, setCursor] = useState(0)
  const [decisions, setDecisions] = useState<PressDecision[]>([])

  const slice = useMemo(() => sliceFor(doc, roundIds, scores), [doc, roundIds, scores])

  // Strokes off the GHIN indexes, the way the tournament does it — see
  // `autoStrokesFor`. It moved out of here alongside `sliceFor` so that the live
  // setup sheet offers the same number this screen does.
  const autoStrokes = useMemo(
    () => autoStrokesFor(doc, a, b, roundIds),
    [doc, a, b, roundIds],
  )

  // Positive means A gives B.
  const strokes =
    strokeMode === 'auto' ? autoStrokes : giver === 'A' ? strokeCount : -strokeCount

  const walk = useMemo(() => {
    if (!a || !b || slice.holes.length === 0) return null
    return runNassau({
      holes: slice.holes,
      grossA: slice.grossFor(a),
      grossB: slice.grossFor(b),
      strokes,
      amountPerBet: amount,
      decisions,
      nameA: playerName(doc, a),
      nameB: playerName(doc, b),
    })
  }, [doc, slice, a, b, strokes, amount, decisions])

  const nameA = playerName(doc, a)
  const nameB = playerName(doc, b)

  // --- setup ---------------------------------------------------------------

  if (!started || !walk) {
    const holeCount = slice.holes.length
    return (
      <>
        <Bar kicker="Cards" title="Score a match" sub="Nassau, with presses" />

        <SectionRule>The match</SectionRule>
        <div className="grid grid-cols-2 gap-2 px-4 py-3">
          <label className="grid gap-1">
            <span className="bc-label text-mute">Player</span>
            <select
              value={a}
              onChange={(e) => setA(e.target.value)}
              className="border-2 border-ink px-2 py-2.5 bc-name text-sm bc-tap bg-paper"
            >
              {doc.players.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.displayName}
                </option>
              ))}
            </select>
          </label>
          <label className="grid gap-1">
            <span className="bc-label text-mute">Against</span>
            <select
              value={b}
              onChange={(e) => setB(e.target.value)}
              className="border-2 border-ink px-2 py-2.5 bc-name text-sm bc-tap bg-paper"
            >
              {doc.players
                .filter((p) => p.id !== a)
                .map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.displayName}
                  </option>
                ))}
            </select>
          </label>
        </div>

        <SectionRule>Strokes</SectionRule>
        <div className="grid grid-cols-2 border-y border-rule">
          {(['auto', 'manual'] as const).map((m) => (
            <button
              key={m}
              type="button"
              onClick={() => setStrokeMode(m)}
              className={`bc-label py-2.5 bc-tap ${
                strokeMode === m ? 'bg-ink text-paper' : 'bg-paper text-mute'
              }`}
            >
              {m === 'auto' ? 'From handicaps' : 'Agreed on the tee'}
            </button>
          ))}
        </div>

        {strokeMode === 'auto' ? (
          <div className="px-4 py-3">
            <p className="bc-name text-sm">
              {autoStrokes === 0
                ? 'Level — no strokes either way.'
                : `${autoStrokes > 0 ? nameA : nameB} gives ${
                    autoStrokes > 0 ? nameB : nameA
                  } ${Math.abs(autoStrokes)}`}
            </p>
            <p className="text-[13px] text-mute leading-snug mt-1.5">
              From the indexes in Setup, run through{' '}
              <span className="bc-num">HI × Slope/113 + (CR − Par)</span> off each
              player&rsquo;s own tee and added up across the rounds picked below. That
              last term already levels different tees, which is what makes a cross-tee
              match fair. Change an index in Setup and this follows.
            </p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 gap-2 px-4 py-3">
              <label className="grid gap-1">
                <span className="bc-label text-mute">Given by</span>
                <select
                  value={giver}
                  onChange={(e) => setGiver(e.target.value as 'A' | 'B')}
                  className="border-2 border-ink px-2 py-2.5 bc-name text-sm bc-tap bg-paper"
                >
                  <option value="A">{nameA}</option>
                  <option value="B">{nameB}</option>
                </select>
              </label>
              <label className="grid gap-1">
                <span className="bc-label text-mute">Strokes</span>
                <input
                  type="number"
                  min={0}
                  max={18}
                  value={strokeCount}
                  onChange={(e) => setStrokeCount(Math.max(0, Number(e.target.value)))}
                  className="border-2 border-ink px-2 py-2 bc-num bc-tap"
                />
              </label>
            </div>
            <p className="px-4 pb-2 text-[13px] text-mute leading-snug">
              Whatever you shook on, regardless of the indexes.
            </p>
          </>
        )}
        <p className="px-4 pb-2 text-[13px] text-mute leading-snug">
          Either way they land on the hardest holes of the rounds picked below, off the
          card&rsquo;s stroke index.
        </p>

        <SectionRule>Holes</SectionRule>
        <ul className="divide-y divide-rule">
          {doc.rounds.map((r) => (
            <li key={r.id}>
              <button
                type="button"
                onClick={() =>
                  setRoundIds((cur) =>
                    cur.includes(r.id) ? cur.filter((x) => x !== r.id) : [...cur, r.id],
                  )
                }
                className={`w-full text-left px-4 py-3 flex items-center gap-3 bc-tap ${
                  roundIds.includes(r.id) ? 'bg-ink text-paper' : ''
                }`}
              >
                <span className="bc-name text-sm flex-1">{r.label}</span>
                <span className="text-[13px] uppercase tracking-wider opacity-70">
                  {courseOf(doc, r)?.name}
                </span>
              </button>
            </li>
          ))}
        </ul>

        <div className="px-4 py-3">
          <label className="grid gap-1 mb-3">
            <span className="bc-label text-mute">Each bet is worth</span>
            <input
              type="number"
              min={0}
              value={amount}
              onChange={(e) => setAmount(Math.max(0, Number(e.target.value)))}
              className="border-2 border-ink px-3 py-2 bc-num bc-tap w-32"
            />
          </label>
          <p className="text-[13px] text-mute leading-snug mb-3">
            {holeCount === 0
              ? 'Pick at least one round.'
              : holeCount === 18
                ? '18 holes — front, back and total, three bets before anyone presses.'
                : /*
                   * Exactly eighteen, not "eighteen or more". Tuesday is three
                   * nines on one date, and ticking all three produced "27 holes
                   * — front, back and total": `baseBets` halves the slice, so
                   * the front became holes 1–13 and the back 14–27. Nobody
                   * agreed to that bet, and it settles in dollars.
                   */
                  `${holeCount} holes — one match, played straight through. A front and a back need exactly eighteen; splitting ${holeCount} would invent two bets nobody agreed to.`}
          </p>
          <button
            type="button"
            disabled={!a || !b || a === b || holeCount === 0}
            onClick={() => {
              setStarted(true)
              setCursor(0)
              setDecisions([])
            }}
            className="bc-tap w-full bg-signal text-paper bc-label py-4 disabled:opacity-30"
          >
            Walk the holes
          </button>
          <button
            type="button"
            onClick={onBack}
            className="bc-tap w-full border-2 border-ink bc-label py-3 mt-2"
          >
            Back to cards
          </button>
        </div>
      </>
    )
  }

  // --- the walk ------------------------------------------------------------

  const step = walk.steps[cursor]!
  const unanswered = step.offers.filter(
    (o) => !decisions.some((d) => d.parentId === o.parentId && d.atIndex === o.atIndex),
  )
  const atEnd = cursor >= walk.steps.length - 1
  const live = step.standings.filter((s) => !s.closed || s.played > 0)

  const answer = (parentId: string, atIndex: number, accepted: boolean) =>
    setDecisions((cur) => [...cur, { parentId, atIndex, accepted }])

  return (
    <>
      <Bar
        kicker={`${nameA} v ${nameB}`}
        // The match position, not the card's hole number — across two nines the
        // card says "3" twice and only the position tells you where you are.
        title={`Hole ${step.position} of ${walk.steps.length}`}
        sub={`${slice.roundLabels[cursor] ?? ''} · hole ${step.holeNo} · par ${step.par} · SI ${step.strokeIndex}`}
        live
      />

      <p className="px-4 py-4 bc-name text-base border-b border-rule">{step.narration}</p>

      <div className="grid grid-cols-2 divide-x divide-rule border-b border-rule">
        {[
          { name: nameA, gross: step.grossA, net: step.netA, pops: step.popsA, won: step.outcome === 1 },
          { name: nameB, gross: step.grossB, net: step.netB, pops: step.popsB, won: step.outcome === -1 },
        ].map((side) => (
          <div key={side.name} className={`px-4 py-3 ${side.won ? 'bg-ink text-paper' : ''}`}>
            <div className="bc-label opacity-70 flex items-center gap-1.5">
              {side.name}
              {side.pops > 0 && (
                <span className="bg-signal text-paper text-[11px] font-extrabold px-1 py-0.5">
                  {side.pops > 1 ? `${side.pops} POPS` : 'POP'}
                </span>
              )}
            </div>
            <div className="bc-num text-3xl mt-1">{side.gross ?? '·'}</div>
            <div className="text-[12px] uppercase tracking-wider opacity-60 mt-0.5">
              {side.net == null ? 'not scored' : `net ${side.net}`}
            </div>
          </div>
        ))}
      </div>

      {unanswered.length > 0 && (
        <div className="bg-signal text-paper">
          {unanswered.map((o) => (
            <div key={`${o.parentId}-${o.atIndex}`} className="px-4 py-3 border-b border-paper/20">
              <p className="bc-name text-sm">
                {o.trailingName} is 2 down on the {o.parentLabel.toLowerCase()}. Press?
              </p>
              <div className="flex gap-2 mt-2">
                <button
                  type="button"
                  onClick={() => answer(o.parentId, o.atIndex, true)}
                  className="bc-tap flex-1 bg-paper text-ink bc-label py-2"
                >
                  Press
                </button>
                <button
                  type="button"
                  onClick={() => answer(o.parentId, o.atIndex, false)}
                  className="bc-tap flex-1 border-2 border-paper bc-label py-2"
                >
                  Pass
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <SectionRule>Where the money is</SectionRule>
      <ul className="divide-y divide-rule">
        {live.map((s) => (
          <li key={s.bet.id} className="px-4 py-2.5 flex items-center gap-3">
            <span className="bc-name text-sm flex-1">
              {s.bet.label}
              {s.closed && <span className="text-mute text-[12px] ml-1.5">final</span>}
              {!s.closed && s.decided && (
                <span className="text-signal text-[12px] ml-1.5">decided</span>
              )}
            </span>
            <span
              className={`bc-num text-base ${
                s.up > 0 ? 'text-ink' : s.up < 0 ? 'text-signal' : 'text-mute'
              }`}
            >
              {s.up === 0 ? 'AS' : `${playerName(doc, s.up > 0 ? a : b)} ${Math.abs(s.up)}`}
            </span>
          </li>
        ))}
      </ul>

      <div className="flex gap-2 p-4">
        <button
          type="button"
          disabled={cursor === 0}
          onClick={() => setCursor((c) => c - 1)}
          className="bc-tap flex-1 border-2 border-ink bc-label py-3 disabled:opacity-30"
        >
          Back
        </button>
        <button
          type="button"
          disabled={unanswered.length > 0 || atEnd}
          onClick={() => setCursor((c) => c + 1)}
          className="bc-tap flex-1 bg-ink text-paper bc-label py-3 disabled:opacity-30"
        >
          {unanswered.length > 0 ? 'Answer first' : 'Next hole'}
        </button>
      </div>

      {atEnd && (
        <>
          <SectionRule>Settle up</SectionRule>
          <ul className="divide-y divide-rule">
            {walk.standings.map((s) => (
              <li key={s.bet.id} className="px-4 py-2.5 flex items-center gap-3">
                <span className="bc-name text-sm flex-1">{s.bet.label}</span>
                <span className="bc-num text-sm">
                  {s.winner == null
                    ? 'open'
                    : s.winner === 0
                      ? 'halved'
                      : `${playerName(doc, s.winner > 0 ? a : b)} +$${amount}`}
                </span>
              </li>
            ))}
          </ul>
          <div className="px-4 py-4 bg-ink text-paper">
            <div className="bc-label opacity-70">
              {walk.ledger.aBets} to {nameA} · {walk.ledger.bBets} to {nameB}
              {walk.ledger.halved > 0 && ` · ${walk.ledger.halved} halved`}
              {walk.ledger.open > 0 && ` · ${walk.ledger.open} unfinished`}
            </div>
            <div className="bc-display text-3xl mt-1">
              {walk.ledger.net === 0
                ? 'All square'
                : `${playerName(doc, walk.ledger.net > 0 ? a : b)} +$${Math.abs(walk.ledger.net)}`}
            </div>
          </div>
        </>
      )}

      <div className="px-4 pb-8 pt-2">
        <button
          type="button"
          onClick={() => setStarted(false)}
          className="bc-tap w-full border-2 border-ink bc-label py-3"
        >
          Start over
        </button>
      </div>
    </>
  )
}
